// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import TransactionRow from './TransactionRow';

export default function TransactionList({
  transactions = [],
  loading = false,
  mobileLimit = 10,
  tabletLimit = 25,
  detailed = false,
  emptyMessage = 'No transactions found.',
  onEdit,
  onDelete,
  showAllByDefault = false,
  isEditMode = false,
  selectedIds = [],
  onToggleSelect = null,
}) {
  const [displayCount, setDisplayCount] = useState(
    showAllByDefault ? transactions.length : mobileLimit
  );

  const rows = useMemo(() => {
    const sorted = [...transactions].sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
    return sorted;
  }, [transactions]);

  // A transfer's two legs (#31) are separate rows in this same list — when
  // both happen to be loaded together, annotate each with the other side's
  // account name so the user doesn't have to hunt for its sibling row to
  // see where the money actually went. Silently omitted if the sibling
  // isn't in the currently loaded page — not an error, just nothing to
  // annotate with yet.
  const siblingAccountByTxnId = useMemo(() => {
    const byGroup = {};
    for (const t of transactions) {
      if (!t.transfer_group) continue;
      (byGroup[t.transfer_group] ||= []).push(t);
    }
    const map = {};
    for (const legs of Object.values(byGroup)) {
      if (legs.length !== 2) continue;
      const [a, b] = legs;
      map[a.id] = b.payment_mode_detail?.name;
      map[b.id] = a.payment_mode_detail?.name;
    }
    return map;
  }, [transactions]);

  // Determine current limit based on screen size
  const getCurrentLimit = () => {
    if (typeof window === 'undefined') return tabletLimit;
    return window.innerWidth < 768 ? mobileLimit : tabletLimit;
  };

  // Display rows based on displayCount or screen limit
  const displayRows = useMemo(() => {
    if (showAllByDefault) return rows;
    return rows.slice(0, displayCount);
  }, [rows, displayCount, showAllByDefault]);

  if (loading) {
    return (
      <div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="px-3 py-3 md:px-4 md:py-3 border-b border-gray-100">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="h-8 w-8 bg-gray-100 animate-pulse rounded-full" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 w-40 bg-gray-100 animate-pulse rounded" />
                <div className="h-3 w-24 bg-gray-100 animate-pulse rounded" />
              </div>
              <div className="h-4 w-16 bg-gray-100 animate-pulse rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <span className="text-4xl mb-2">💳</span>
        <p className="text-sm text-gray-500">{emptyMessage}</p>
        <p className="text-xs text-gray-400 mt-1">
          Add some transactions to see them here
        </p>
      </div>
    );
  }

  const hasMore = displayRows.length < rows.length;
  const currentLimit = getCurrentLimit();

  return (
    <div>
      {displayRows.map((txn) => (
        <TransactionRow
          key={txn.id}
          transaction={txn}
          detailed={detailed}
          onEdit={onEdit}
          onDelete={onDelete}
          isEditMode={isEditMode}
          isSelected={selectedIds.includes(txn.id)}
          onToggleSelect={onToggleSelect}
          siblingAccountName={siblingAccountByTxnId[txn.id]}
        />
      ))}
      <div className="px-3 py-3 md:px-4 md:py-3 text-xs text-gray-500 border-t border-gray-100 bg-gray-50 space-y-3">
        <div>
          Showing {displayRows.length}
          {rows.length > displayRows.length && ` of ${rows.length}`}
          {' '}transactions
        </div>

        {/* Load More Button */}
        {hasMore && !showAllByDefault && (
          <button
            onClick={() => setDisplayCount((prev) => prev + currentLimit)}
            className="w-full px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <ChevronDown size={16} />
            Load More ({rows.length - displayRows.length} remaining)
          </button>
        )}
      </div>
    </div>
  );
}
